import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Ventes from "@/pages/Ventes";
import Stock from "@/pages/Stock";
import Charges from "@/pages/Charges";
import Parametres from "@/pages/Parametres";
import Produits from "@/pages/Produits";
import Pertes from "@/pages/Pertes";
import Livraisons from "@/pages/Livraisons";
import Tournees from "@/pages/Tournees";
import Livreur from "@/pages/Livreur";
import SuiviDemo from "@/pages/SuiviDemo";
import Suivi from "@/pages/Suivi";
import SuiviCommandePublic from "@/pages/SuiviCommandePublic";
import SchemaDemo from "@/pages/SchemaDemo";
import BonLivraisonDemo from "@/pages/BonLivraisonDemo";
import Clients from "@/pages/Clients";
import VenteDepot from "@/pages/VenteDepot";import Devis from "@/pages/Devis";
import Impayes from "@/pages/Impayes";
import Veille from "@/pages/Veille";
import Taches from "@/pages/Taches";
import Social from "@/pages/Social";
import Marketing from "@/pages/Marketing";
import Zones from "@/pages/Zones";
import Communications from "@/pages/Communications";
import Parrainages from "@/pages/Parrainages";
import ParrainageLanding from "@/pages/ParrainageLanding";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import Corbeille from "@/pages/Corbeille";
import Vehicules from "@/pages/Vehicules";
import AssistantIA from "@/pages/AssistantIA";
import Arrivages from "@/pages/Arrivages";
import Rappels from "@/pages/Rappels";
import RendezVous from "@/pages/RendezVous";
import PriseRdv from "@/pages/PriseRdv";
import Confirmation from "@/pages/Confirmation";
import PreCommandePublic from "@/pages/PreCommandePublic";
import B2BPortal from "@/pages/B2BPortal";
import B2BAdmin from "@/pages/B2BAdmin";
import B2BLogin from "@/pages/B2BLogin";
import B2BEspace from "@/pages/B2BEspace";
import B2BRegister from "@/pages/B2BRegister";
import AppsHome from "@/pages/AppsHome";
import AuditLog from "@/pages/AuditLog";

// Components
import Sidebar from "@/components/Sidebar";
import BottomNav from "@/components/BottomNav";
import { BadgesProvider } from "@/hooks/useBadges";
import NotificationBell from "@/components/NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------- Permissions helpers ----------
// Full catalog of app pages + their default route.
export const PAGE_CATALOG = [
  { key: "dashboard", label: "Accueil", path: "/" },
  { key: "ventes", label: "Ventes", path: "/ventes" },
  { key: "stock", label: "Stock", path: "/stock" },
  { key: "produits", label: "Produits", path: "/produits" },
  { key: "pertes", label: "Pertes", path: "/pertes" },
  { key: "livraisons", label: "Livraisons", path: "/livraisons" },
  { key: "tournees", label: "Tournées", path: "/tournees" },
  { key: "zones", label: "Carte des ventes", path: "/zones" },
  { key: "charges", label: "Charges", path: "/charges" },
  { key: "clients", label: "Clients", path: "/clients" },
  { key: "rappels", label: "À rappeler", path: "/rappels" },
  { key: "rdv", label: "Rendez-vous", path: "/rendez-vous" },
  { key: "devis", label: "Devis", path: "/devis" },
  { key: "impayes", label: "Impayés", path: "/impayes" },
  { key: "veille", label: "Veille", path: "/veille" },
  { key: "taches", label: "Tâches", path: "/taches" },
  { key: "social", label: "Réseaux Sociaux", path: "/social" },
  { key: "marketing", label: "Marketing", path: "/marketing" },
  { key: "communications", label: "Communications", path: "/communications" },
  { key: "parrainages", label: "Parrainages", path: "/parrainages" },
  { key: "corbeille", label: "Corbeille", path: "/corbeille" },
  { key: "parametres", label: "Paramètres", path: "/parametres" },
  { key: "vehicules", label: "Véhicules", path: "/vehicules" },
  { key: "arrivages", label: "Arrivages", path: "/arrivages" },
  { key: "livreur", label: "Mode Livreur", path: "/livreur" },
  { key: "ai_chef", label: "Assistant IA", path: "/assistant-ia" },
];

export const hasPermission = (user, pageKey) => {
  if (!user) return false;
  const perms = user.permissions || [];
  return perms.includes("*") || perms.includes(pageKey);
};

export const firstAccessiblePath = (user) => {
  if (!user) return "/login";
  if (hasPermission(user, "dashboard")) return "/";
  // Special: if only livreur → /livreur
  for (const p of PAGE_CATALOG) {
    if (hasPermission(user, p.key)) return p.path;
  }
  return "/login";
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
// - permission: page key from PAGE_CATALOG (e.g. "dashboard", "ventes", "livreur")
//   If the user does not have the permission, they are redirected to their first accessible page.
// - role: legacy, kept for /livreur route with "all"
const ProtectedRoute = ({ children, role = "all", permission = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // New permission-based gating
  if (permission) {
    if (!hasPermission(user, permission)) {
      return <Navigate to={firstAccessiblePath(user)} replace />;
    }
    return children;
  }

  // Legacy role-based gating (kept for /livreur "all" route)
  return children;
};

// Auto-redirect users without dashboard access to their first accessible page
const HomeRouter = ({ children }) => {
  const { user } = useAuth();
  if (user && !hasPermission(user, "dashboard")) {
    return <Navigate to={firstAccessiblePath(user)} replace />;
  }
  return children;
};

// Layout with Sidebar
const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1024;
  });

  return (
    <BadgesProvider>
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main
          className={`flex-1 transition-all duration-300 ml-0 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}
          style={{ paddingBottom: "calc(4rem + env(safe-area-inset-bottom))" }}
        >
          {/* Top bar with notifications - safe-area aware for iOS PWA */}
          <div
            className="sticky top-0 z-30 flex items-center justify-end gap-2 px-4 sm:px-6 md:px-8 py-2 bg-slate-50/80 backdrop-blur-sm border-b border-slate-100"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}
          >
            <NotificationBell />
          </div>
          <div className="p-4 sm:p-6 md:p-8">
            {children}
          </div>
        </main>
        {/* Bottom nav mobile — "Plus" ouvre le drawer Sidebar */}
        <BottomNav onOpenMore={() => setSidebarOpen(true)} moreActive={sidebarOpen} />
      </div>
    </BadgesProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute permission="dashboard">
                <AppLayout>
                  <AppsHome />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/statistiques"
            element={
              <ProtectedRoute permission="dashboard">
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ventes"
            element={
              <ProtectedRoute permission="ventes">
                <AppLayout>
                  <Ventes />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vente-depot"
            element={
              <ProtectedRoute permission="ventes">
                <AppLayout>
                  <VenteDepot />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock"
            element={
              <ProtectedRoute permission="stock">
                <AppLayout>
                  <Stock />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/charges"
            element={
              <ProtectedRoute permission="charges">
                <AppLayout>
                  <Charges />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parametres"
            element={
              <ProtectedRoute permission="parametres">
                <AppLayout>
                  <Parametres />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/produits"
            element={
              <ProtectedRoute permission="produits">
                <AppLayout>
                  <Produits />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pertes"
            element={
              <ProtectedRoute permission="pertes">
                <AppLayout>
                  <Pertes />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/livraisons"
            element={
              <ProtectedRoute permission="livraisons">
                <AppLayout>
                  <Livraisons />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournees"
            element={
              <ProtectedRoute permission="tournees">
                <AppLayout>
                  <Tournees />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/livreur" element={<ProtectedRoute permission="livreur"><Livreur /></ProtectedRoute>} />
          <Route path="/assistant-ia" element={<ProtectedRoute permission="ai_chef"><AppLayout><AssistantIA /></AppLayout></ProtectedRoute>} />
          <Route path="/arrivages" element={<ProtectedRoute permission="arrivages"><AppLayout><Arrivages /></AppLayout></ProtectedRoute>} />
          <Route path="/zones" element={<ProtectedRoute permission="zones"><AppLayout><Zones /></AppLayout></ProtectedRoute>} />
          <Route
            path="/clients"
            element={
              <ProtectedRoute permission="clients">
                <AppLayout>
                  <Clients />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/devis"
            element={
              <ProtectedRoute permission="devis">
                <AppLayout>
                  <Devis />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/impayes"
            element={
              <ProtectedRoute permission="impayes">
                <AppLayout>
                  <Impayes />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/veille"
            element={
              <ProtectedRoute permission="veille">
                <AppLayout>
                  <Veille />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/taches"
            element={
              <ProtectedRoute permission="taches">
                <AppLayout>
                  <Taches />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/social"
            element={
              <ProtectedRoute permission="social">
                <AppLayout>
                  <Social />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing"
            element={
              <ProtectedRoute permission="marketing">
                <AppLayout>
                  <Marketing />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/communications"
            element={
              <ProtectedRoute permission="communications">
                <AppLayout>
                  <Communications />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parrainages"
            element={
              <ProtectedRoute permission="parrainages">
                <AppLayout>
                  <Parrainages />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/corbeille"
            element={
              <ProtectedRoute permission="corbeille">
                <AppLayout>
                  <Corbeille />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicules"
            element={
              <ProtectedRoute permission="vehicules">
                <AppLayout>
                  <Vehicules />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Public parrainage landing */}
          <Route path="/p/:code" element={<ParrainageLanding />} />
          <Route path="/rappels" element={<ProtectedRoute permission="rappels"><AppLayout><Rappels /></AppLayout></ProtectedRoute>} />
          <Route path="/rendez-vous" element={<ProtectedRoute permission="rdv"><AppLayout><RendezVous /></AppLayout></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute permission="parametres"><AppLayout><AuditLog /></AppLayout></ProtectedRoute>} />
          <Route path="/prendre-rdv" element={<PriseRdv />} />
          <Route path="/confirmation/:id" element={<Confirmation />} />
          <Route path="/precommande" element={<PreCommandePublic />} />
          <Route path="/b2b/:code" element={<B2BPortal />} />
          <Route path="/b2b-login" element={<B2BLogin />} />
          <Route path="/b2b-register" element={<B2BRegister />} />
          <Route path="/b2b-espace" element={<B2BEspace />} />
          <Route
            path="/b2b-admin"
            element={
              <ProtectedRoute permission="parametres">
                <AppLayout>
                  <B2BAdmin />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Short link for SMS: /s/{token} → /suivi-commande-topcocoon?token=X */}
          <Route path="/s/:token" element={<ShortLinkRedirect />} />
          <Route path="/suivi-demo" element={<SuiviDemo />} />
          <Route path="/suivi/:token" element={<Suivi />} />
          <Route path="/suivi-commande-topcocoon" element={<SuiviCommandePublic />} />
          <Route path="/c/:numero" element={<SuiviCommandePublic />} />
          <Route path="/schema-demo" element={<SchemaDemo />} />
          <Route path="/bon-livraison-demo" element={<BonLivraisonDemo />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
